/* ============================================
   WORK LAYOUT
   The Creative Orchestra - GEMINI Framework
   Layout wrapper for individual work pages
   with dynamic theme application
   ============================================ */

import { useEffect, useMemo } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import Navigation from '@components/common/Navigation';
import Footer from '@components/common/Footer';
import AudioController from '@components/common/AudioController';
import ScrollToTop, { ScrollToTopButton } from '@components/common/ScrollToTop';
import { useTheme } from '@context/ThemeContext';
import { useRouteTheme } from '@hooks/useWorkTheme';
import { getWorkById } from '@data';

/**
 * Work Layout Component
 * Specialized layout for work pages with:
 * - Dynamic theme based on work ID
 * - Themed background gradients
 * - Work-specific accent colors
 * - Smooth theme transitions
 * 
 * @param {Object} props
 * @param {React.ReactNode} props.children - Page content
 * @param {string} props.workId - Work identifier (optional, uses route param if not provided)
 */
const WorkLayout = ({ children, workId: propWorkId }) => {
  const { workId: routeWorkId } = useParams();
  const location = useLocation();
  const workId = propWorkId || routeWorkId;

  // Get work data
  const workData = useMemo(() => getWorkById(workId), [workId]);

  // Apply theme for this work
  const { theme, isTransitioning } = useRouteTheme(workId);

  // Scroll to top on route change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  // Page transition variants
  const pageVariants = {
    initial: {
      opacity: 0,
      scale: 0.98,
    },
    animate: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.5,
        ease: [0.25, 0.46, 0.45, 0.94],
      },
    },
    exit: {
      opacity: 0,
      scale: 1.02,
      transition: {
        duration: 0.3,
        ease: [0.25, 0.46, 0.45, 0.94],
      },
    },
  };

  // Background style with theme gradient
  const backgroundStyle = useMemo(() => ({
    background: theme?.gradients?.backgroundDark || 
      'linear-gradient(180deg, #0a0a0f 0%, #0f1419 50%, #0a0a0f 100%)',
    '--theme-primary': theme?.colors?.primary || '#E8F0FE',
    '--theme-secondary': theme?.colors?.secondary || '#D4E5FC',
    '--theme-accent': theme?.colors?.accent || '#4285F4',
    '--theme-accent-rgb': theme?.cssVariables?.['--theme-accent-rgb'] || '66, 133, 244',
    '--glow-color': theme?.cssVariables?.['--glow-color'] || 'rgba(66, 133, 244, 0.4)',
  }), [theme]);

  return (
    <div 
      className="min-h-screen flex flex-col text-white work-theme"
      style={backgroundStyle}
    >
      {/* Scroll to top utility */}
      <ScrollToTop />

      {/* Navigation with work context */}
      <Navigation currentWork={workData} />

      {/* Audio Controller */}
      <AudioController />

      {/* Themed background accents */}
      <WorkBackgroundAccents theme={theme} />

      {/* Main Content */}
      <AnimatePresence mode="wait">
        <motion.main
          key={location.pathname}
          variants={pageVariants}
          initial="initial"
          animate="animate"
          exit="exit"
          className="flex-grow pt-20 relative z-10"
        >
          {children}
        </motion.main>
      </AnimatePresence>

      {/* Footer */}
      <Footer />

      {/* Scroll to Top Button */}
      <ScrollToTopButton threshold={400} />

      {/* Theme transition overlay */}
      <AnimatePresence>
        {isTransitioning && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-50 pointer-events-none"
            style={{
              background: `radial-gradient(circle at center, ${theme?.colors?.accent}10, transparent 70%)`,
            }}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

/**
 * Work Background Accents
 * Decorative gradient orbs and effects based on work theme
 */
const WorkBackgroundAccents = ({ theme }) => {
  if (!theme) return null;

  const accentColor = theme.colors?.accent || '#4285F4';
  const accentRgb = theme.cssVariables?.['--theme-accent-rgb'] || '66, 133, 244';

  return (
    <div className="fixed inset-0 pointer-events-none overflow-hidden z-0">
      {/* Top-right accent glow */}
      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 1, delay: 0.2 }}
        className="absolute -top-40 -right-40 w-96 h-96 rounded-full blur-3xl"
        style={{
          background: `radial-gradient(circle, rgba(${accentRgb}, 0.15) 0%, transparent 70%)`,
        }}
      />

      {/* Bottom-left accent glow */}
      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 1, delay: 0.4 }}
        className="absolute -bottom-40 -left-40 w-80 h-80 rounded-full blur-3xl"
        style={{
          background: `radial-gradient(circle, rgba(${accentRgb}, 0.1) 0%, transparent 70%)`,
        }}
      />

      {/* Center subtle gradient */}
      <div 
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full"
        style={{
          background: `radial-gradient(ellipse at center, rgba(${accentRgb}, 0.03) 0%, transparent 50%)`,
        }}
      />

      {/* Grid pattern overlay */}
      <div 
        className="absolute inset-0 opacity-[0.02]"
        style={{
          backgroundImage: `
            linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)
          `,
          backgroundSize: '50px 50px',
        }}
      />
    </div>
  );
};

/**
 * Work Section Component
 * Consistent section wrapper for work page content
 */
export const WorkSection = ({ 
  children, 
  className = '', 
  id,
  noPadding = false,
}) => {
  return (
    <section 
      id={id}
      className={`
        ${noPadding ? '' : 'py-16 md:py-24 px-4 md:px-8'}
        ${className}
      `}
    >
      <div className="max-w-7xl mx-auto">
        {children}
      </div>
    </section>
  );
};

/**
 * Work Content Container
 * Narrower container for text content
 */
export const WorkContent = ({ children, className = '' }) => {
  return (
    <div className={`max-w-4xl mx-auto ${className}`}>
      {children}
    </div>
  );
};

/**
 * Work Title Component
 * Themed title with gradient and glow
 */
export const WorkTitle = ({ 
  children, 
  subtitle,
  className = '',
}) => {
  const { activeTheme } = useTheme();

  return (
    <div className={`text-center mb-12 ${className}`}>
      <motion.h1
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="text-4xl md:text-6xl lg:text-7xl font-display font-bold mb-4"
        style={{
          background: activeTheme?.gradients?.text || 
            'linear-gradient(135deg, #ffffff 0%, #a8d4ff 50%, #ffffff 100%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text',
          textShadow: activeTheme?.glows?.text,
        }}
      >
        {children}
      </motion.h1>
      
      {subtitle && (
        <motion.p
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="text-xl md:text-2xl text-gray-300 font-light"
        >
          {subtitle}
        </motion.p>
      )}
    </div>
  );
};

/**
 * Work Card Component
 * Themed card with glass effect
 */
export const WorkCard = ({ 
  children, 
  className = '',
  hover = true,
}) => {
  const { activeTheme } = useTheme();
  const accentRgb = activeTheme?.cssVariables?.['--theme-accent-rgb'] || '255, 255, 255';

  return (
    <motion.div
      whileHover={hover ? { y: -5, scale: 1.02 } : {}}
      transition={{ duration: 0.3 }}
      className={`
        rounded-2xl p-6 md:p-8
        bg-white/5 backdrop-blur-sm
        border border-white/10
        ${hover ? 'hover:border-white/20 hover:bg-white/10' : ''}
        transition-colors duration-300
        ${className}
      `}
      style={{
        boxShadow: hover ? `0 0 30px rgba(${accentRgb}, 0.05)` : 'none',
      }}
    >
      {children}
    </motion.div>
  );
};

/**
 * Work Badge Component
 * Themed badge/pill
 */
export const WorkBadge = ({ 
  children, 
  variant = 'default',
  className = '',
}) => {
  const { activeTheme } = useTheme();
  
  const variants = {
    default: {
      background: `rgba(${activeTheme?.cssVariables?.['--theme-accent-rgb'] || '255, 255, 255'}, 0.1)`,
      color: activeTheme?.colors?.accent || '#ffffff',
      border: `1px solid rgba(${activeTheme?.cssVariables?.['--theme-accent-rgb'] || '255, 255, 255'}, 0.2)`,
    },
    solid: {
      background: activeTheme?.colors?.accent || '#4285F4',
      color: '#ffffff',
      border: 'none',
    },
    outline: {
      background: 'transparent',
      color: activeTheme?.colors?.accent || '#ffffff',
      border: `1px solid ${activeTheme?.colors?.accent || '#ffffff'}`,
    },
  };

  const style = variants[variant] || variants.default;

  return (
    <span 
      className={`
        inline-flex items-center px-3 py-1 rounded-full
        text-sm font-medium
        ${className}
      `}
      style={style}
    >
      {children}
    </span>
  );
};

export default WorkLayout;
